import { mapQueryString, Observable, unmapQueryString } from "@configura/web-utilities";
import { CfgIOManager } from "./CfgIOManager.js";
import { CfgWindowEventManager } from "./CfgWindowEventManager.js";
/**
 * How the history is updated.
 * @param DoNotWrite Only listens to initial URL-values.
 * @param Replace Replaces the current history frame at updates.
 * @param Push Adds history frames at updates.
 * @param ReplaceAndUpdateUrl Replaces the current history frame at updates and updates the browser URL
 * @param PushAndUpdateUrl Adds history frames at updates and updates the browser URL
 */
export var HistoryMode;
(function (HistoryMode) {
    HistoryMode[HistoryMode["DoNotWrite"] = 0] = "DoNotWrite";
    HistoryMode[HistoryMode["Replace"] = 1] = "Replace";
    HistoryMode[HistoryMode["Push"] = 2] = "Push";
    HistoryMode[HistoryMode["ReplaceAndUpdateUrl"] = 3] = "ReplaceAndUpdateUrl";
    HistoryMode[HistoryMode["PushAndUpdateUrl"] = 4] = "PushAndUpdateUrl";
})(HistoryMode || (HistoryMode = {}));
/**
 * This class is used to coordinate writing and reading to the browser history.
 * It handles messages sent from the connectors.
 */
export class CfgHistoryManager extends CfgWindowEventManager {
    constructor() {
        super();
        this._aggregatedQsKeyValues = new Map();
        this._urlUpdateObservable = new Observable();
        this.eventType = "popstate";
    }
    static get instance() {
        if (this._instance === undefined) {
            this._instance = new CfgHistoryManager();
        }
        return this._instance;
    }
    /**
     * @returns The current browser URL updated with the latest updates from
     *          the Connectors.
     */
    getUrl() {
        return CfgHistoryManager._makeUpdatedUrl(this._aggregatedQsKeyValues);
    }
    /**
     * Listen for updated URL:s. This doesn't have to mean the URL in the
     * browser has been updated.
     */
    listenForUrl(listener) {
        this._urlUpdateObservable.listen(listener);
    }
    /**
     * Stop listen.
     */
    stopListenForUrl(listener) {
        this._urlUpdateObservable.stopListen(listener);
    }
    /**
     * Write to the history
     */
    send(messageKey, data) {
        const { qsKeyValues, message, mode } = data;
        for (const keyValue of qsKeyValues) {
            this._aggregatedQsKeyValues.set(keyValue[0], keyValue[1]);
        }
        this._urlUpdateObservable.notifyAll(this.getUrl());
        if (mode === HistoryMode.DoNotWrite) {
            return;
        }
        // Initial data is before user interaction has happened
        const initial = message.initial;
        const url = CfgHistoryManager._makeUpdatedUrl(qsKeyValues);
        // At initial we do not replace the URL as we presumable have the defaults
        const writeUrl = initial ||
            !(mode === HistoryMode.PushAndUpdateUrl || mode === HistoryMode.ReplaceAndUpdateUrl)
            ? null
            : url;
        // ...but we do replace the state so that we can in the future browse back to here
        const newState = CfgHistoryManager._makeUpdatedState(window.history.state, message, messageKey);
        if (initial ||
            mode === HistoryMode.Replace ||
            mode === HistoryMode.ReplaceAndUpdateUrl ||
            this.receiveInProgress) {
            window.history.replaceState(newState, "", writeUrl);
        }
        else {
            window.history.pushState(newState, "", writeUrl);
        }
    }
    /**
     * Takes the current in browser URL and updates it with the provided Query String values.
     * Old Query String is replaced.
     */
    static _makeUpdatedUrl(qsKeyValues) {
        const qs = this._makeUpdatedQueryString(qsKeyValues);
        return `${window.location.origin}${window.location.pathname}${qs ? "?" : ""}${qs}${window.location.hash}`;
    }
    /**
     * Returns the current in browser Query String updated with the key values provided.
     * If a value is undefined in the passed map it will be deleted from the Query String.
     */
    static _makeUpdatedQueryString(qsKeyValues) {
        const currentKeyValues = this.currentQsKeyValues();
        for (const [key, value] of qsKeyValues) {
            if (value === undefined) {
                currentKeyValues.delete(key);
            }
            else {
                currentKeyValues.set(key, value);
            }
        }
        return unmapQueryString(currentKeyValues);
    }
    /**
     * @returns The current in browser Query String as a Map
     */
    static currentQsKeyValues() {
        return mapQueryString(window.location.search);
    }
    /**
     * If the passed currentState is a CfgIOContainer it will update it with the
     * passed message. If not it will create a new CfgIOContainer with the message.
     */
    static _makeUpdatedState(currentState, message, messageKey) {
        let messages = {};
        if (CfgIOManager.isIOContainer(currentState)) {
            messages = currentState.messages;
        }
        messages[messageKey] = message;
        return CfgIOManager.makeContainer(messages);
    }
    getDataFromEvent(event) {
        return event.state;
    }
    static getMessageFromCurrentHistoryState(messageKey) {
        const state = window.history.state;
        if (!CfgIOManager.isIOContainer(state)) {
            return undefined;
        }
        if (!CfgIOManager.hasIOContainerMessageKey(state, messageKey)) {
            return undefined;
        }
        return CfgIOManager.getMessageFromIOContainer(state, messageKey);
    }
}
